/**
 * CombatAnchors — WHICH POINT of an entity combat measures from.
 *
 * `MeleeCone.isInCone` takes two bare `{x, y}` points and does honest
 * trigonometry on them. Every bug this module exists to prevent comes from the
 * two call sites disagreeing about which point of a body those numbers name.
 *
 * The two anchors, as shipped (verified at runtime on `/combatlab`, not read
 * off a comment):
 *
 *   PLAYER (both sides)      the HITBOX CENTRE, `position + size/2`.
 *                            `player.position` / `player.x/y` is the TOP-LEFT
 *                            of the 40x40 box; combat adds half of it through
 *                            {@link playerCombatPoint}.
 *
 *   MOB (both sides)         the SPRITE CENTRE, `schema + size/2`.
 *                            Client: `MobManager.registerMobInEcs` fills the
 *                            ECS transform from `mob.x/mob.y`, which the `Mob`
 *                            constructor centres; `EcsCombatTargets` hands
 *                            those to the cone.
 *                            Server: `AreaTargeting` centres `npc.x/npc.y`
 *                            through {@link mobCombatPoint}.
 *
 * ── COMBAT-MELEE-CONE-03 (2026-07-27): why the server moved ────────────────
 * Until this landed the SERVER scanned raw `npc.x/npc.y` — the schema TOP-LEFT.
 * That is a translation, not a scale: testing `|origin - topLeft| <= R` is the
 * same as testing `|(origin + (w/2, h/2)) - centre| <= R`, so every AoE in the
 * game was a correctly-sized blast centred (w/2, h/2) DOWN-RIGHT of where it
 * was drawn. Facing right the server therefore out-reached the drawn cone;
 * facing left it fell short. DJ's 83s `/combatlab` session caught the right-
 * hand half of that: `lab_ruler_right_240` lost 170 HP across ~7 swings of a
 * 190px warrior cone that never once predicted it.
 *
 * Three reasons the CENTRE is the correct anchor, not the corner:
 *   1. The corner makes reach depend on FACING. A body's top-left is nearer
 *      when you approach from the left and further when you approach from the
 *      right, so the same swing at the same distance hits or whiffs depending
 *      on which way you turned. The centre is the only anchor invariant to
 *      approach direction — which is what a radial/facing-relative shape needs.
 *   2. The error SCALES WITH MOB SIZE: 25px for a snail (40x30), 39px for a
 *      pig (60x50), 111px for pig_mom (180x130). No constant fudge can absorb
 *      it, and the biggest bodies — bosses — are the most wrong.
 *   3. The drawn cone is the promise. `MeleeConeOverlayRenderer` draws from
 *      `CombatManager.getMeleeConePlan`, which hit-tests centres; aligning
 *      authority to the drawing is correct-by-construction, whereas aligning
 *      the drawing to the corner would render a shape that lurches with facing.
 *
 * ── COMBAT-MELEE-CONE-04 (2026-07-27): the other half of the pair ──────────
 * CONE-03 left a KNOWN residual: the ORIGIN was still the player's hitbox
 * top-left, so every cone and every player-originated AoE started 20px up-left
 * of his visual centre. It desynced nothing (both sides read the same corner),
 * but it made the shipped numbers lie about the shape:
 *
 *   - reach depended on FACING, the same defect CONE-03 removed from the target
 *     side. Measured from the body the player is looking at, a 190px warrior
 *     cone reached 170px to the RIGHT (the origin sits 20px behind the leading
 *     edge of travel) and 210px to the LEFT. One number, two reaches.
 *   - the wedge sat on the HEAD, not the body: the origin was 20px above the
 *     centre, so a target slightly below the player read as a miss the
 *     arithmetic said should connect.
 *
 * These two errors used to partially CANCEL. Before CONE-03 the effective AoE
 * centre was `player.position + (30, 25)` for a 60x50 mob — the player's +20/+20
 * corner error and the mob's +30/+25 corner error nearly annihilating, leaving
 * ~10px right / 5px down. That cancellation was an accident of one mob size
 * (it was 20px off for a snail and 70px off for pig_mom), and CONE-03 removed
 * the mob half. This lands the player half, so neither side is compensating for
 * the other any more.
 *
 * Both sides move TOGETHER, as one commit. Moving one alone re-opens exactly
 * the divergence class CONE-03 closed, and
 * `AreaTargeting.parity.test.ts` fails if anything ever does.
 *
 * NOTE the player anchor also governs players as TARGETS, not just as origins.
 * Player↔player distance used to be corner→corner, which is EXACTLY right
 * whenever both boxes are the same size — so PvP AoE and `handleHealPlayer`
 * were correct by cancellation. Moving only the origin would have introduced a
 * (20, 20) error there. Origin and target anchor are the same function for that
 * reason.
 *
 * Pure functions, no imports: safe from client entities, map authoring, server
 * code and tests alike.
 */

export interface EntityBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CombatPoint {
  x: number;
  y: number;
}

/**
 * The point combat measures a mob at — its sprite centre. `Mob` publishes this
 * as `mob.x/mob.y`, `MobManager` mirrors it into the ECS transform,
 * `CombatManager.getMeleeConePlan` hit-tests against it, and (since
 * COMBAT-MELEE-CONE-03) `AreaTargeting` resolves the server's area scan
 * against it too.
 *
 * Requires exact dimensions. Server code reading Colyseus schema rows should
 * call {@link mobCombatPoint}, which tolerates a dimensionless row.
 */
export function mobSpriteCenter(box: EntityBox): CombatPoint {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

/**
 * {@link mobSpriteCenter} for a Colyseus `NPC` schema row — the server's read
 * path. Same anchor, but tolerant of a row with no dimensions.
 *
 * A live `NPC` always has `width`/`height` (the schema declares defaults of
 * 60/50 and `EntitySpawnerService` writes the mob config's real size), so the
 * fallback never fires in production. It exists for synthetic rows in tests and
 * probes, where the honest reading of "no width" is a POINT entity whose corner
 * and centre coincide — not a mob that should be silently shifted by an assumed
 * 60x50. Anything that wants the real offset must supply real dimensions.
 */
export function mobCombatPoint(npc: { x: number; y: number; width?: number; height?: number }): CombatPoint {
  return mobSpriteCenter({
    x: npc.x,
    y: npc.y,
    width: npc.width ?? 0,
    height: npc.height ?? 0,
  });
}

/**
 * Inverse of {@link mobSpriteCenter}: sprite centre → schema top-left, the
 * anchor `npc.x/npc.y`, map authoring, and the server's area scan all use.
 */
export function mobTopLeftFromCenter(center: CombatPoint, size: { width: number; height: number }): CombatPoint {
  return { x: center.x - size.width / 2, y: center.y - size.height / 2 };
}

/**
 * The player's collision box, and the SSOT for its size.
 *
 * `CharacterFactory.createHelix2000Character` — the ONE constructor every
 * player, remote player and dev character goes through — stamps these onto the
 * character, and `PhysicsUtils.getCharacterBounds` falls back to them. Nothing
 * in the codebase mutates `player.width`/`player.height` afterwards, so 40x40
 * is the shipped hitbox for every class.
 *
 * It lives here because the server has NO player dimensions at all: the
 * Colyseus `Player` schema carries `x`/`y` but no `width`/`height` (unlike
 * `NPC`, which carries both). Without a shared constant the server could not
 * find the centre of the body it is resolving damage against, and the two sides
 * would be back to disagreeing about which point of a player the numbers name.
 * Change the player's size HERE and both sides move together.
 */
export const PLAYER_HITBOX = { width: 40, height: 40 } as const;

/**
 * The point combat measures a player at — the centre of the hitbox.
 *
 * Used for BOTH roles (see the CONE-04 note above): the ORIGIN a cone or
 * player-originated AoE is cast from, and the TARGET point a player is hit at.
 *
 * Requires exact dimensions. Callers holding a live character or a schema row
 * should use {@link playerCombatPoint}, which supplies the shipped box.
 */
export function playerHitboxCenter(box: EntityBox): CombatPoint {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

/**
 * {@link playerHitboxCenter} for whatever shape of player the caller is
 * holding — the client's `Helix2000Character` (`{ position, width, height }`),
 * the server's Colyseus row (`{ x, y }`, no dimensions), or a bare top-left
 * point from a map/probe.
 *
 * DELIBERATELY UNLIKE {@link mobCombatPoint}, a missing dimension falls back to
 * {@link PLAYER_HITBOX} rather than to zero. For a mob, "no width" is a
 * synthetic point entity and inventing 60x50 would silently shift it; for a
 * player, "no width" is the NORMAL server case (the schema simply does not
 * carry the field) and every one of those bodies really is 40x40. Defaulting to
 * zero there would leave the server measuring the corner — the bug — on every
 * single hit.
 */
export function playerCombatPoint(player: {
  x?: number;
  y?: number;
  position?: { x: number; y: number };
  width?: number;
  height?: number;
}): CombatPoint {
  return playerHitboxCenter({
    x: player.position?.x ?? player.x ?? 0,
    y: player.position?.y ?? player.y ?? 0,
    width: player.width && player.width > 0 ? player.width : PLAYER_HITBOX.width,
    height: player.height && player.height > 0 ? player.height : PLAYER_HITBOX.height,
  });
}

/**
 * A player as a reach TARGET: the centred body `BodyReach.reachToBody` measures
 * to — {@link playerCombatPoint} plus the hitbox size.
 *
 * Plan 21 B4: mobs have been measured to their body edge since the plan 07/08
 * reach audit, while a player target stayed a centre point because the
 * Colyseus row carries no size. Every player shares {@link PLAYER_HITBOX}, so
 * the size needs no wire field; a caller holding a live character with its own
 * box gets that box. Use it wherever a PLAYER is the target of a reach, range
 * or area check, on the client and the server alike, so the two agree.
 */
export function playerReachBody(player: Parameters<typeof playerCombatPoint>[0]): CombatPoint & { width: number; height: number } {
  return {
    ...playerCombatPoint(player),
    width: player.width && player.width > 0 ? player.width : PLAYER_HITBOX.width,
    height: player.height && player.height > 0 ? player.height : PLAYER_HITBOX.height,
  };
}

/**
 * Inverse of {@link playerHitboxCenter}: hitbox centre → the top-left that
 * `player.position` / `player.x/y`, spawn points and map authoring all use.
 */
export function playerTopLeftFromCenter(
  center: CombatPoint,
  size: { width: number; height: number } = PLAYER_HITBOX,
): CombatPoint {
  return { x: center.x - size.width / 2, y: center.y - size.height / 2 };
}
